import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Heatmap Layer Component
const HeatmapLayer = ({ points }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!points || points.length === 0) return;
    
    // Create heat layer using L.heatLayer if available
    if (L.heatLayer) {
      const heatData = points.map(p => [p.lat, p.lng, p.intensity || 0.5]);
      const heat = L.heatLayer(heatData, { radius: 25, blur: 15, maxZoom: 10 });
      heat.addTo(map);
      return () => map.removeLayer(heat);
    }
  }, [map, points]);
  
  return null;
};

// Intelligence Dashboard Component
const IntelligenceDashboard = ({ token }) => {
  const [dashboard, setDashboard] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [selectedFugitive, setSelectedFugitive] = useState(null);
  const [fugitivePrediction, setFugitivePrediction] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [scanningNews, setScanningNews] = useState(false);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intelligence/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token]);

  // Fetch global heatmap
  const fetchHeatmap = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intelligence/heatmap/global?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHeatmapData(data);
      }
    } catch (error) {
      console.error('Error fetching heatmap:', error);
    }
  }, [token]);

  // Fetch high-risk predictions
  const fetchPredictions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intelligence/predictions/high-risk?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPredictions(data.high_risk_fugitives || []);
      }
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  }, [token]);

  // Fetch individual fugitive prediction
  const fetchFugitivePrediction = async (fugitiveId) => {
    try {
      const response = await fetch(`${API_URL}/api/intelligence/predictions/fugitive/${fugitiveId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFugitivePrediction(data);
        setSelectedFugitive(fugitiveId);
      }
    } catch (error) {
      console.error('Error fetching fugitive prediction:', error);
    }
  };

  // Trigger news scan
  const triggerNewsScan = async () => {
    setScanningNews(true);
    try {
      const response = await fetch(`${API_URL}/api/intelligence/news/scan-all?limit=20`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Scanned ${data.fugitives_scanned} fugitives, found ${data.articles_found} new articles`);
        fetchDashboard();
      }
    } catch (error) {
      console.error('Error scanning news:', error);
    } finally {
      setScanningNews(false);
    }
  };

  // Generate predictions
  const generatePredictions = async () => {
    setGeneratingPredictions(true);
    try {
      const response = await fetch(`${API_URL}/api/intelligence/predictions/generate?limit=50`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Generated ${data.predictions_generated} predictions, ${data.high_risk_fugitives?.length || 0} high-risk`);
        fetchDashboard();
        fetchPredictions();
      }
    } catch (error) {
      console.error('Error generating predictions:', error);
    } finally {
      setGeneratingPredictions(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchHeatmap(), fetchPredictions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchHeatmap, fetchPredictions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 min-h-screen text-white">
      {/* Header Card */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                  🎯
                </span>
                Intelligence Center
              </h1>
              <p className="text-slate-400 text-sm mt-1 ml-13">AI-powered fugitive tracking analytics</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={triggerNewsScan}
                disabled={scanningNews}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg flex items-center gap-2 disabled:opacity-50 text-sm font-medium transition-all"
              >
                {scanningNews ? (
                  <><span className="animate-spin">⏳</span> Scanning...</>
                ) : (
                  <>📰 Scan News</>
                )}
              </button>
              <button
                onClick={generatePredictions}
                disabled={generatingPredictions}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg flex items-center gap-2 disabled:opacity-50 text-sm font-medium transition-all"
              >
                {generatingPredictions ? (
                  <><span className="animate-spin">⏳</span> Generating...</>
                ) : (
                  <>🧠 Generate Predictions</>
                )}
              </button>
            </div>
          </div>

          {/* Tabs - Clean inline style */}
          <div className="flex gap-1 mt-6 -mb-px">
            {[
              { key: 'overview', label: 'Overview', icon: '📊' },
              { key: 'heatmap', label: 'Heatmap', icon: '🗺️' },
              { key: 'predictions', label: 'Predictions', icon: '🧠' },
              { key: 'news', label: 'News', icon: '📰' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm font-medium transition-all flex items-center gap-2 border-b-2 ${
                  activeTab === tab.key
                    ? 'text-cyan-400 border-cyan-400 bg-slate-900/50'
                    : 'text-slate-400 border-transparent hover:text-white hover:border-slate-600'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-6 py-6">

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Predictions"
              value={dashboard.summary?.total_predictions || 0}
              icon="🧠"
              color="purple"
            />
            <StatCard
              title="High Risk Fugitives"
              value={dashboard.summary?.high_risk_fugitives || 0}
              icon="⚠️"
              color="red"
            />
            <StatCard
              title="News Articles"
              value={dashboard.summary?.total_news_articles || 0}
              icon="📰"
              color="blue"
            />
            <StatCard
              title="Active Hotspots"
              value={dashboard.summary?.active_hotspots || 0}
              icon="🔥"
              color="orange"
            />
          </div>

          {/* High Risk Fugitives */}
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-red-400 mb-4">🚨 Critical Flight Risk Fugitives</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboard.high_risk_fugitives?.slice(0, 6).map((fugitive) => (
                <div
                  key={fugitive.fugitive_id}
                  onClick={() => fetchFugitivePrediction(fugitive.fugitive_id)}
                  className="bg-slate-700 rounded-lg p-4 cursor-pointer hover:bg-slate-600 transition-colors border-l-4 border-red-500"
                >
                  <h3 className="font-bold text-white">{fugitive.fugitive_name}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      fugitive.flight_risk_level === 'CRITICAL' ? 'bg-red-600' :
                      fugitive.flight_risk_level === 'HIGH' ? 'bg-orange-600' :
                      'bg-yellow-600'
                    }`}>
                      {fugitive.flight_risk_level}
                    </span>
                    <span className="text-slate-400 text-sm">
                      Risk: {(fugitive.flight_risk_score * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* High Probability Zones */}
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-cyan-400 mb-4">📍 High Probability Zones</h2>
            <div className="space-y-3">
              {dashboard.high_probability_zones?.slice(0, 5).map((zone, idx) => (
                <div key={idx} className="bg-slate-700 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium">{zone.reasoning}</p>
                      <p className="text-slate-400 text-sm">
                        Lat: {zone.lat?.toFixed(4)}, Lng: {zone.lng?.toFixed(4)}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-cyan-400 font-bold">
                        {zone.fugitives?.length || 0} fugitives
                      </span>
                      <p className="text-slate-400 text-sm">
                        Score: {zone.total_probability?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {zone.fugitives?.slice(0, 4).map((f, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-600 rounded text-xs text-slate-300">
                        {f.name}
                      </span>
                    ))}
                    {zone.fugitives?.length > 4 && (
                      <span className="px-2 py-1 bg-slate-600 rounded text-xs text-slate-400">
                        +{zone.fugitives.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Heatmap Tab */}
      {activeTab === 'heatmap' && (
        <div className="space-y-6">
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-cyan-400 mb-4">🗺️ Tip Activity Heatmap</h2>
            <div className="h-[500px] rounded-lg overflow-hidden">
              <MapContainer
                center={[39.8283, -98.5795]}
                zoom={4}
                style={{ height: '100%', width: '100%' }}
                className="rounded-lg"
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {heatmapData?.heatmap_points?.map((point, idx) => (
                  <CircleMarker
                    key={idx}
                    center={[point.lat, point.lng]}
                    radius={Math.max(5, point.intensity * 20)}
                    fillColor={point.intensity > 0.7 ? '#ef4444' : point.intensity > 0.4 ? '#f97316' : '#22d3ee'}
                    fillOpacity={0.6}
                    stroke={false}
                  >
                    <Popup>
                      <div className="text-slate-900">
                        <strong>Tips: {point.tip_count}</strong><br/>
                        Intensity: {(point.intensity * 100).toFixed(0)}%<br/>
                        Verified: {point.verified_count || 0}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
                {dashboard?.high_probability_zones?.map((zone, idx) => (
                  <CircleMarker
                    key={`zone-${idx}`}
                    center={[zone.lat, zone.lng]}
                    radius={15}
                    fillColor="#a855f7"
                    fillOpacity={0.4}
                    color="#a855f7"
                    weight={2}
                  >
                    <Popup>
                      <div className="text-slate-900">
                        <strong>{zone.reasoning}</strong><br/>
                        Fugitives: {zone.fugitives?.length || 0}<br/>
                        Score: {zone.total_probability?.toFixed(2)}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
            <div className="mt-4 flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-cyan-500"></div>
                <span className="text-slate-400">Low Activity</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                <span className="text-slate-400">Medium Activity</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="text-slate-400">High Activity</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                <span className="text-slate-400">Prediction Zone</span>
              </div>
            </div>
          </div>

          {/* Regional Summary */}
          {heatmapData?.regional_summary?.length > 0 && (
            <div className="bg-slate-800 rounded-xl p-6">
              <h2 className="text-xl font-bold text-cyan-400 mb-4">📊 Regional Activity</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {heatmapData.regional_summary.map((region) => (
                  <div key={region.region} className="bg-slate-700 rounded-lg p-4 text-center">
                    <h3 className="text-lg font-bold capitalize text-white">{region.region}</h3>
                    <p className="text-3xl font-bold text-cyan-400">{region.tip_count}</p>
                    <p className="text-slate-400 text-sm">tips</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Predictions Tab */}
      {activeTab === 'predictions' && (
        <div className="space-y-6">
          {/* Predictions List */}
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-purple-400 mb-4">🧠 AI Predictions</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="pb-3 text-slate-400">Fugitive</th>
                    <th className="pb-3 text-slate-400">Risk Level</th>
                    <th className="pb-3 text-slate-400">Flight Risk</th>
                    <th className="pb-3 text-slate-400">Primary Zone</th>
                    <th className="pb-3 text-slate-400">Confidence</th>
                    <th className="pb-3 text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {predictions.map((pred) => (
                    <tr key={pred.fugitive_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 font-medium">{pred.fugitive_name}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          pred.flight_risk_level === 'CRITICAL' ? 'bg-red-600' :
                          pred.flight_risk_level === 'HIGH' ? 'bg-orange-600' :
                          pred.flight_risk_level === 'MODERATE' ? 'bg-yellow-600' :
                          'bg-green-600'
                        }`}>
                          {pred.flight_risk_level}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-slate-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                pred.flight_risk_score > 0.8 ? 'bg-red-500' :
                                pred.flight_risk_score > 0.6 ? 'bg-orange-500' :
                                'bg-yellow-500'
                              }`}
                              style={{ width: `${pred.flight_risk_score * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-slate-400">
                            {(pred.flight_risk_score * 100).toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-sm text-slate-400">
                        {pred.primary_zone?.reasoning?.substring(0, 30) || 'N/A'}...
                      </td>
                      <td className="py-3">
                        <span className="text-cyan-400">
                          {((pred.confidence_score || 0) * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => fetchFugitivePrediction(pred.fugitive_id)}
                          className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 rounded text-sm"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Selected Fugitive Prediction Detail */}
          {fugitivePrediction && (
            <div className="bg-slate-800 rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white">{fugitivePrediction.fugitive_name}</h2>
                  <p className="text-slate-400">Analysis ID: {fugitivePrediction.analysis_id}</p>
                </div>
                <button
                  onClick={() => setFugitivePrediction(null)}
                  className="text-slate-400 hover:text-white"
                >
                  ✕ Close
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-700 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Flight Risk</p>
                  <p className="text-2xl font-bold text-red-400">
                    {(fugitivePrediction.flight_risk_score * 100).toFixed(0)}%
                  </p>
                  <span className={`px-2 py-1 rounded text-xs ${
                    fugitivePrediction.flight_risk_level === 'CRITICAL' ? 'bg-red-600' : 'bg-orange-600'
                  }`}>
                    {fugitivePrediction.flight_risk_level}
                  </span>
                </div>
                <div className="bg-slate-700 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Crime Category</p>
                  <p className="text-2xl font-bold text-cyan-400 capitalize">
                    {fugitivePrediction.crime_category?.replace('_', ' ')}
                  </p>
                  <p className="text-slate-400 text-sm">
                    Pattern: {fugitivePrediction.behavior_profile}
                  </p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">International Risk</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {((fugitivePrediction.international_risk || 0) * 100).toFixed(0)}%
                  </p>
                  <p className="text-slate-400 text-sm">
                    Search radius: {fugitivePrediction.search_radius_km}km
                  </p>
                </div>
              </div>

              {/* Predicted Locations Map */}
              <div className="h-[400px] rounded-lg overflow-hidden mb-6">
                <MapContainer
                  center={[
                    fugitivePrediction.primary_zone?.lat || 39.8283,
                    fugitivePrediction.primary_zone?.lng || -98.5795
                  ]}
                  zoom={5}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  {fugitivePrediction.predicted_locations?.map((loc, idx) => (
                    <CircleMarker
                      key={idx}
                      center={[loc.lat, loc.lng]}
                      radius={Math.max(8, loc.probability * 30)}
                      fillColor={idx === 0 ? '#ef4444' : loc.source === 'border_analysis' ? '#a855f7' : '#22d3ee'}
                      fillOpacity={0.6}
                      color={idx === 0 ? '#ef4444' : '#22d3ee'}
                      weight={idx === 0 ? 3 : 1}
                    >
                      <Popup>
                        <div className="text-slate-900">
                          <strong>{loc.reasoning}</strong><br/>
                          Probability: {(loc.probability * 100).toFixed(0)}%<br/>
                          Radius: {loc.radius_km}km<br/>
                          Source: {loc.source}
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>

              {/* Recommendations */}
              <div className="bg-slate-700 rounded-lg p-4">
                <h3 className="font-bold text-cyan-400 mb-3">📋 Recommended Actions</h3>
                <ul className="space-y-2">
                  {fugitivePrediction.recommended_actions?.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-300">
                      <span className="text-cyan-400">•</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* News Tab */}
      {activeTab === 'news' && (
        <div className="space-y-6">
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-blue-400 mb-4">📰 Recent News Alerts</h2>
            {dashboard?.recent_news_alerts?.length > 0 ? (
              <div className="space-y-4">
                {dashboard.recent_news_alerts.map((article, idx) => (
                  <div key={idx} className="bg-slate-700 rounded-lg p-4 hover:bg-slate-600 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white font-medium hover:text-cyan-400 transition-colors"
                        >
                          {article.title}
                        </a>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                          <span>📌 {article.source}</span>
                          <span>📅 {article.published_at}</span>
                          <span className="px-2 py-0.5 bg-slate-600 rounded">
                            Relevance: {((article.relevance_score || 0) * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm ml-4"
                      >
                        Read →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <p className="text-4xl mb-4">📰</p>
                <p>No news articles found</p>
                <button
                  onClick={triggerNewsScan}
                  className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  Scan for News
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color }) => {
  const colors = {
    purple: 'from-purple-500 to-purple-700',
    red: 'from-red-500 to-red-700',
    blue: 'from-blue-500 to-blue-700',
    orange: 'from-orange-500 to-orange-700',
    cyan: 'from-cyan-500 to-cyan-700',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-6 shadow-lg`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white/80 text-sm">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
        </div>
        <span className="text-3xl">{icon}</span>
      </div>
    </div>
  );
};

export default IntelligenceDashboard;
